import {
  createContext,
  ReactNode,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import Keycloak from "keycloak-js";
import * as Sentry from "@sentry/react";

import {
  KeycloakContextType,
  ActionMap,
  AuthState,
  AuthUser,
  USER_TYPES,
} from "@/types/auth";
import { keycloakConfig } from "@/config";
import {
  clearStoredSentryUser,
  setStoredSentryUser,
} from "@/utils/sentryUserStorage";
import { registerTokenAccessor } from "@/lib/auth";
import {
  identifyUser,
  initializePostHog,
  registerUserRole,
  resetUser,
} from "@/lib/posthog";
import { clearPersistedPropertySelections } from "@/utils/propertySelectionStorage";
import { resolveUserRole } from "@/utils/userRole";

const AUTH_ACTIONS = {
  INITIALIZE: "INITIALIZE",
  SIGN_IN: "SIGN_IN",
  SIGN_OUT: "SIGN_OUT",
  UPDATE_TOKEN: "UPDATE_TOKEN",
} as const;

const AUTH_CONFIG = {
  TOKEN_MIN_VALIDITY_SECONDS: 70,
  TOKEN_REFRESH_INTERVAL_MS: 60000,
  // A refresh failure that isn't a clear "your token is dead" response (400
  // invalid_grant / 401) could just as easily be a transient network blip or
  // a momentary Keycloak hiccup — signing out on the very first one of those
  // costs the user their whole session over nothing. Only sign out once this
  // many *consecutive* refresh attempts have failed; any successful refresh
  // in between resets the count.
  MAX_CONSECUTIVE_REFRESH_FAILURES: 3,
  KEYCLOAK_INIT_OPTIONS: {
    onLoad: "check-sso",
    pkceMethod: "S256",
    checkLoginIframe: false,
    // Without this, keycloak-js's `check-sso` falls back to a full top-level
    // redirect through Keycloak and back (see keycloak-js's `doLogin`) on
    // every app load instead of a silent background iframe check — visibly
    // flashes/reloads the page, and any hiccup in that redirect round trip
    // (e.g. `error=login_required`) renders as "signed out" even when the
    // SSO session is still alive. `public/silent-check-sso.html` already
    // ships in this repo but was never wired up to be used.
  },
  LOGIN_SUCCESS_REDIRECT_URI: "/auth/post-login",
} as const;

let keycloak: Keycloak | null = null;
let moduleDispatch: ((action: KeycloakActions) => void) | null = null;

// keycloak-js (26.x) rejects updateToken() with its own `NetworkError`
// class, which carries the HTTP status on `.response.status` (a `Response`
// object) — not a flat `.status` like an Axios error. `NetworkError` isn't
// exported from keycloak-js's type declarations, so this checks both shapes
// by duck-typing instead of importing/instanceof-checking an undeclared class.
const extractErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== "object" || error === null) return undefined;
  const flatStatus = (error as { status?: unknown }).status;
  if (typeof flatStatus === "number") return flatStatus;
  const nestedStatus = (error as { response?: { status?: unknown } }).response
    ?.status;
  return typeof nestedStatus === "number" ? nestedStatus : undefined;
};

// 400 is what the OIDC token endpoint returns for `invalid_grant` (an
// expired/revoked refresh token, per RFC 6749 §5.2); some Keycloak versions
// surface 401 for the same case. Either way, this is Keycloak explicitly
// telling us the session is dead — not something a retry can recover from,
// unlike a network error or a momentary server hiccup (which won't carry a
// status at all, or will carry an unrelated one).
const isDefiniteAuthFailure = (error: unknown): boolean => {
  const status = extractErrorStatus(error);
  return status === 400 || status === 401;
};

const captureAuthException = (
  error: unknown,
  context: string,
  level: "warning" | "error" = "warning"
) => {
  Sentry.captureException(error, {
    tags: {
      feature: "authentication",
      context,
      source: "keycloak",
    },
    level,
  });
};

const initialState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  accessToken: null,
};

type AuthActionTypes = {
  [AUTH_ACTIONS.INITIALIZE]: {
    isAuthenticated: boolean;
    user: AuthUser;
  };
  [AUTH_ACTIONS.SIGN_IN]: {
    user: AuthUser;
  };
  [AUTH_ACTIONS.SIGN_OUT]: undefined;
  [AUTH_ACTIONS.UPDATE_TOKEN]: {
    accessToken: string;
  };
};

type KeycloakActions =
  ActionMap<AuthActionTypes>[keyof ActionMap<AuthActionTypes>];

// Pure state transitions only — Sentry/PostHog identity side effects live in
// applySignedInIdentity/clearIdentity below, called at each dispatch site
// instead of here. A reducer that reaches out to third-party SDKs makes
// identification depend on reducer execution (React can invoke reducers more
// than once per dispatch, e.g. under StrictMode) and makes this state
// transition harder to test or replay in isolation.
const reducer = (state: AuthState, action: KeycloakActions) => {
  switch (action.type) {
    case AUTH_ACTIONS.INITIALIZE:
      return {
        ...state,
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user: action.payload.user,
      };

    case AUTH_ACTIONS.SIGN_IN:
      return {
        ...state,
        isAuthenticated: Boolean(action.payload.user),
        isInitialized: true,
        user: action.payload.user,
      };

    case AUTH_ACTIONS.SIGN_OUT:
      return { ...state, isAuthenticated: false, user: null };

    case AUTH_ACTIONS.UPDATE_TOKEN:
      return { ...state, accessToken: action.payload.accessToken };

    default:
      return state;
  }
};

// Sentry + PostHog identity side effects for a successful sign-in — called
// right before dispatching SIGN_IN, not from inside the reducer.
const applySignedInIdentity = (user: NonNullable<AuthUser>) => {
  const distinctId = user.sub || user.id;
  const sentryUser = { email: user.email, id: distinctId, name: user.name };
  Sentry.setUser(sentryUser);
  setStoredSentryUser(sentryUser);
  identifyUser(distinctId, user.email ? { email: user.email } : undefined);
  registerUserRole(user.user_role ?? "unknown");
};

// Sentry + PostHog identity teardown — called right before dispatching
// INITIALIZE (unauthenticated) or SIGN_OUT, not from inside the reducer.
const clearIdentity = () => {
  Sentry.setUser(null);
  clearStoredSentryUser();
  resetUser();
};

const AuthContext = createContext<KeycloakContextType | null>(null);

// Module-scoped — only references the keycloak singleton, no React state
const signIn = async () => {
  if (!keycloak) return;
  try {
    await keycloak.login({
      redirectUri:
        window.location.origin + AUTH_CONFIG.LOGIN_SUCCESS_REDIRECT_URI,
    });
  } catch (e) {
    captureAuthException(e, "login-attempt");
    throw e;
  }
};

const signOut = async () => {
  if (!keycloak) return;

  clearIdentity();
  moduleDispatch?.({ type: AUTH_ACTIONS.SIGN_OUT });
  // sessionStorage survives the redirect below within the same tab — clear it
  // so the next user to sign in doesn't inherit this user's property selection.
  clearPersistedPropertySelections();

  try {
    await keycloak.logout({ redirectUri: window.location.origin });
  } catch (error) {
    captureAuthException(error, "logout-attempt");
  }
};

const getAccessToken = () => keycloak?.token ?? null;

function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    moduleDispatch = dispatch;
    return () => {
      moduleDispatch = null;
    };
  }, [dispatch]);

  useEffect(() => {
    let refreshInterval: ReturnType<typeof setInterval> | undefined;
    // Reset on every successful refresh; only actually signs the user out
    // once this reaches MAX_CONSECUTIVE_REFRESH_FAILURES (see the catch
    // handler below) — a lone transient failure shouldn't cost the session.
    let consecutiveRefreshFailures = 0;

    const initialize = async () => {
      if (keycloak) return;

      try {
        keycloak = new Keycloak({
          realm: keycloakConfig.realm,
          url: keycloakConfig.url,
          clientId: keycloakConfig.clientId,
        });

        const authenticated = await keycloak.init(
          AUTH_CONFIG.KEYCLOAK_INIT_OPTIONS
        );

        // Deferred until keycloak.init() resolves so the first PostHog
        // pageview/replay capture happens after Keycloak has already
        // stripped the OAuth code/state callback params from the URL,
        // instead of racing that cleanup. Must stay ahead of
        // applySignedInIdentity below, which identifies/registers against
        // the initialized instance.
        initializePostHog();

        if (authenticated && keycloak.token) {
          // Let API clients read keycloak.token at request-time via interceptor
          registerTokenAccessor(() => keycloak?.token ?? null);

          dispatch({
            type: AUTH_ACTIONS.UPDATE_TOKEN,
            payload: { accessToken: keycloak.token },
          });

          refreshInterval = setInterval(() => {
            keycloak
              ?.updateToken(AUTH_CONFIG.TOKEN_MIN_VALIDITY_SECONDS)
              .then((refreshed: boolean) => {
                // A successful call either way (whether or not a refresh was
                // actually needed) means the session is fine — clear any
                // transient-failure count from earlier ticks.
                consecutiveRefreshFailures = 0;
                if (refreshed && keycloak?.token) {
                  dispatch({
                    type: AUTH_ACTIONS.UPDATE_TOKEN,
                    payload: { accessToken: keycloak.token },
                  });
                }
              })
              .catch((refreshError: unknown) => {
                if (isDefiniteAuthFailure(refreshError)) {
                  // Keycloak has explicitly told us the refresh token is
                  // dead (expired/revoked) — no retry will fix that, so this
                  // is expected/routine, not worth a captured *exception*.
                  // Still captured as a low-severity message (a breadcrumb
                  // alone wouldn't reach Sentry without a followup captured
                  // event to attach to) so we have frequency/timing data
                  // while tracking down sessions dying well inside the
                  // configured SSO Session Idle/Max window.
                  Sentry.captureMessage(
                    "Token refresh: definite auth failure, signing out",
                    {
                      level: "info",
                      tags: {
                        feature: "authentication",
                        context: "token-refresh-definite-failure",
                        source: "keycloak",
                      },
                      extra: { status: extractErrorStatus(refreshError) },
                    }
                  );
                  clearIdentity();
                  dispatch({ type: AUTH_ACTIONS.SIGN_OUT });
                  clearPersistedPropertySelections();
                  return;
                }

                // Anything else (network error, momentary server hiccup,
                // an error shape we don't recognize) could well resolve
                // itself on the next tick — don't treat one failed attempt
                // as "the session is dead".
                consecutiveRefreshFailures += 1;
                captureAuthException(refreshError, "token-refresh");

                if (
                  consecutiveRefreshFailures <
                  AUTH_CONFIG.MAX_CONSECUTIVE_REFRESH_FAILURES
                ) {
                  return;
                }

                // Failed enough times in a row that this no longer looks
                // transient — give up and sign out, same as a definite
                // auth failure above.
                captureAuthException(
                  refreshError,
                  "token-refresh-exhausted-retries",
                  "error"
                );
                clearIdentity();
                dispatch({ type: AUTH_ACTIONS.SIGN_OUT });
                // Same reasoning as the manual signOut() path: whoever signs
                // in next in this tab shouldn't inherit this user's property
                // selection from sessionStorage.
                clearPersistedPropertySelections();
              });
          }, AUTH_CONFIG.TOKEN_REFRESH_INTERVAL_MS);

          try {
            const userInfo = await keycloak.loadUserInfo();
            const claims = keycloak.tokenParsed as
              | Record<string, unknown>
              | undefined;
            const rawUserType = claims?.["user_type"];
            // A missing or blank claim must never read as internal.
            const userType =
              typeof rawUserType === "string" && rawUserType.trim()
                ? rawUserType.trim()
                : USER_TYPES.EXTERNAL;
            const rawOrgScopes = claims?.["user_org_scopes"];
            const userOrgScopes = Array.isArray(rawOrgScopes)
              ? rawOrgScopes.filter((s): s is string => typeof s === "string")
              : [];
            // Role lives in the access token's realm/client role claims, not in
            // the userinfo response — same lift-off-the-token shape as user_type.
            const userRole = resolveUserRole(keycloak.tokenParsed);
            const user = {
              ...userInfo,
              user_type: userType,
              user_org_scopes: userOrgScopes,
              user_role: userRole,
            };
            applySignedInIdentity(user);
            dispatch({ type: AUTH_ACTIONS.SIGN_IN, payload: { user } });
          } catch (e) {
            captureAuthException(e, "user-info-load");
            clearIdentity();
            dispatch({
              type: AUTH_ACTIONS.INITIALIZE,
              payload: { isAuthenticated: false, user: null },
            });
          }
        } else {
          clearIdentity();
          dispatch({
            type: AUTH_ACTIONS.INITIALIZE,
            payload: { isAuthenticated: false, user: null },
          });
        }
      } catch (e) {
        captureAuthException(e, "keycloak-init", "error");
        // Degrade to signed-out rather than rethrowing: initialize() is called
        // without a .catch(), so a rethrow became an unhandled rejection that
        // left isInitialized false forever — an infinite loader, and no
        // analytics from anything gated on it.
        clearIdentity();
        dispatch({
          type: AUTH_ACTIONS.INITIALIZE,
          payload: { isAuthenticated: false, user: null },
        });
      } finally {
        // Safety net for the failure path only — an auth bootstrap failure
        // (unreachable auth server, wrong realm, origin not in the Keycloak
        // client's allowlist) would otherwise silently mean zero events
        // portal-wide. No-ops on the success path, where the call above
        // already ran: initializePostHog is idempotent.
        initializePostHog();
      }
    };

    initialize();
    return () => {
      if (refreshInterval !== undefined) clearInterval(refreshInterval);
    };
  }, []);

  // Excludes accessToken — consumers use getAccessToken() at call-time
  const { isAuthenticated, isInitialized, user } = state;
  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      isInitialized,
      user,
      signIn,
      signOut,
      getAccessToken,
    }),
    [isAuthenticated, isInitialized, user]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
